<template>
  <ArchiveModal
    :open="open"
    :title="file ? file.name : 'Preview'"
    :subtitle="subtitle"
    size="xl"
    flush
    @close="$emit('close')"
  >
    <template #head>
      <div class="ar-preview-actions">
        <button
          v-if="list.length > 1"
          type="button"
          class="ar-icon-btn"
          aria-label="Previous file"
          title="Previous (←)"
          @click="step(-1)"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <span v-if="list.length > 1" class="ar-muted">{{ index + 1 }} / {{ list.length }}</span>
        <button
          v-if="list.length > 1"
          type="button"
          class="ar-icon-btn"
          aria-label="Next file"
          title="Next (→)"
          @click="step(1)"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
        <button v-if="google" type="button" class="btn btn-sm btn-outline-primary" @click="openGoogle">
          <i class="bi bi-google mr-1"></i>{{ google }}
        </button>
        <button type="button" class="ar-icon-btn" aria-label="Copy link" title="Copy link" @click="copyLink">
          <i class="bi bi-link-45deg"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-primary" :disabled="downloading" @click="download">
          <span v-if="downloading" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="bi bi-download mr-1"></i>Download
        </button>
      </div>
    </template>

    <div class="ar-preview" tabindex="-1" @keydown.left="step(-1)" @keydown.right="step(1)">
      <div v-if="loading" class="ar-preview-state">
        <span class="spinner-border spinner-border-sm mr-2"></span> Opening…
      </div>
      <div v-else-if="error" class="ar-preview-state">
        <i class="bi bi-exclamation-triangle"></i> {{ error }}
      </div>
      <iframe v-else-if="kind === 'pdf'" :src="url" :title="file.name" class="ar-preview-frame"></iframe>
      <img v-else-if="kind === 'image'" :src="url" :alt="file.name" class="ar-preview-image" />
      <pre v-else-if="kind === 'text'" class="ar-preview-text">{{ text }}</pre>
      <div v-else-if="file" class="ar-preview-state none">
        <i class="bi" :class="type.icon" aria-hidden="true"></i>
        <strong>{{ google ? `Opens in ${googleApp}` : "No preview for this kind of file" }}</strong>
        <span>{{ type.label }} · {{ formatSize(file.size) }}</span>
        <div class="ar-preview-none-actions">
          <button v-if="google" type="button" class="btn btn-primary" @click="openGoogle">
            <i class="bi bi-google mr-1"></i>{{ google }}
          </button>
          <button type="button" class="btn" :class="google ? 'btn-light' : 'btn-primary'" :disabled="downloading" @click="download">
            <span v-if="downloading" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="bi bi-download mr-1"></i>Download
          </button>
        </div>
      </div>
    </div>
  </ArchiveModal>
</template>

<script>
import axios from "axios";
import ArchiveModal from "./ArchiveModal.vue";
import {
  GOOGLE_APPS,
  archiveLink,
  canPreview,
  copyText,
  downloadFile,
  errorMessage,
  fileType,
  formatDateTime,
  formatSize,
  googleAction,
  openInGoogle,
} from "../archiveHelpers";

/** Shows a PDF, image or text file (others offer a download); arrows step through the folder's files. */
export default {
  name: "ArchivePreviewModal",
  components: { ArchiveModal },
  props: {
    open: { type: Boolean, default: false },
    file: { type: Object, default: null },
    // The files around it, for previous / next.
    files: { type: Array, default: () => [] },
  },
  data() {
    return { url: null, text: "", loading: false, error: "", downloading: false };
  },
  computed: {
    list() {
      return this.files;
    },
    google() {
      return googleAction(this.file);
    },
    googleApp() {
      return (this.file && GOOGLE_APPS[this.file.google_kind]) || "Google";
    },
    type() {
      return fileType(this.file && this.file.extension);
    },
    index() {
      return this.file ? this.list.findIndex((f) => f.id === this.file.id) : -1;
    },
    kind() {
      const mime = (this.file && this.file.mime) || "";
      if (mime === "application/pdf") return "pdf";
      if (mime.startsWith("image/")) return "image";
      if (mime === "text/plain") return "text";
      return "";
    },
    subtitle() {
      if (!this.file) return "";
      const parts = [formatSize(this.file.size)];
      if (this.file.owner_name) parts.push(`uploaded by ${this.file.owner_name}`);
      if (this.file.created_at) parts.push(formatDateTime(this.file.created_at));
      return parts.join(" · ");
    },
  },
  watch: {
    open(value) {
      if (value) this.load();
      else this.release();
    },
    // Stepping to another file. (Opening is handled above: open and file change
    // together then, and loading twice would fetch the file twice.)
    file(next, prev) {
      if (this.open && next && prev && next.id !== prev.id) this.load();
    },
  },
  beforeDestroy() {
    this.release();
  },
  methods: {
    release() {
      if (this.url) URL.revokeObjectURL(this.url);
      this.url = null;
      this.text = "";
    },
    formatSize,
    async load() {
      if (!this.file) return;
      this.release();
      this.error = "";
      if (!canPreview(this.file)) return;
      this.loading = true;
      const id = this.file.id;
      try {
        const res = await axios.get(`api/archives/files/${id}/preview`, { responseType: "blob" });
        if (!this.file || this.file.id !== id) return;
        const blob = new Blob([res.data], { type: this.file.mime });
        if (this.kind === "text") this.text = await blob.text();
        else this.url = URL.createObjectURL(blob);
      } catch (err) {
        this.error = await errorMessage(err, "This file couldn't be opened.");
      } finally {
        this.loading = false;
      }
    },
    step(delta) {
      if (this.list.length < 2) return;
      const next = this.list[(this.index + delta + this.list.length) % this.list.length];
      this.$emit("navigate", next);
    },
    async download() {
      this.downloading = true;
      try {
        await downloadFile(this.file);
      } catch (err) {
        toastr.error(await errorMessage(err, "The download didn't start."), "Download");
      } finally {
        this.downloading = false;
      }
    },
    async openGoogle() {
      try {
        await openInGoogle(this.file);
      } catch (err) {
        toastr.error(await errorMessage(err, "The file couldn't be opened in Google."), "Google");
      }
    },
    async copyLink() {
      const copied = await copyText(archiveLink(this.file));
      if (copied) toastr.success("Link copied. It opens for people with access to this cabinet.", "Copy link");
      else toastr.error("The link couldn't be copied.", "Copy link");
    },
  },
};
</script>
